export const GLB_COMPRESSION_PRESETS = [
  {
    value: "standard",
    label: "标准压缩",
    description: "平衡文件体积和模型精度，适合作为默认上传策略",
    meshoptLevel: "high",
    quantizePosition: 14,
    quantizeNormal: 8,
    quantizeTexcoord: 10,
    quantizeColor: 8,
    quantizeWeight: 8,
    quantizeGeneric: 10,
    textureResize: null,
    textureFormat: "",
    simplifyRatio: 1,
    simplifyError: 0,
  },
  {
    value: "strong",
    label: "强力压缩",
    description:
      "进一步压缩网格和贴图，会把贴图转为 WebP 并限制到 2048，兼顾观感与体积",
    meshoptLevel: "high",
    quantizePosition: 12,
    quantizeNormal: 8,
    quantizeTexcoord: 9,
    quantizeColor: 7,
    quantizeWeight: 7,
    quantizeGeneric: 9,
    textureResize: [2048, 2048],
    textureFormat: "webp",
    simplifyRatio: 1,
    simplifyError: 0,
  },
  {
    value: "extreme",
    label: "极限压缩",
    description:
      "尽量冲击 4-6MB，会把贴图转为 WebP、限制到 1024，并进行适度减面，画质和兼容性风险更高",
    meshoptLevel: "high",
    quantizePosition: 10,
    quantizeNormal: 7,
    quantizeTexcoord: 8,
    quantizeColor: 6,
    quantizeWeight: 6,
    quantizeGeneric: 8,
    textureResize: [1024, 1024],
    textureFormat: "webp",
    simplifyRatio: 0.6,
    simplifyError: 0.01,
  },
];

export function getGlbCompressionPreset(value) {
  return (
    GLB_COMPRESSION_PRESETS.find((item) => item.value === value) ||
    GLB_COMPRESSION_PRESETS[0]
  );
}

export async function compressGlbFile(file, presetInput) {
  const preset = getGlbCompressionPreset(
    presetInput && presetInput.value ? presetInput.value : presetInput
  );
  const [
    { WebIO },
    { ALL_EXTENSIONS },
    { dedup, prune, meshopt, textureCompress, simplify },
    meshoptimizer,
  ] = await Promise.all([
    import("@gltf-transform/core"),
    import("@gltf-transform/extensions"),
    import("@gltf-transform/functions"),
    import("meshoptimizer"),
  ]);
  const { MeshoptEncoder, MeshoptDecoder, MeshoptSimplifier } = meshoptimizer;
  const io = new WebIO()
    .registerExtensions(ALL_EXTENSIONS)
    .registerDependencies({
      "meshopt.decoder": MeshoptDecoder,
      "meshopt.encoder": MeshoptEncoder,
    });
  const inputBytes = new Uint8Array(await file.arrayBuffer());
  const document = await io.readBinary(inputBytes);
  await MeshoptEncoder.ready;
  const transforms = [dedup(), prune()];

  if (preset.simplifyRatio && preset.simplifyRatio < 1) {
    await MeshoptSimplifier.ready;
    transforms.push(
      simplify({
        simplifier: MeshoptSimplifier,
        ratio: preset.simplifyRatio,
        error: preset.simplifyError || 0.01,
      })
    );
    transforms.push(prune());
  }

  if (preset.textureFormat || preset.textureResize) {
    transforms.push(
      textureCompress({
        targetFormat: preset.textureFormat || undefined,
        resize: preset.textureResize || undefined,
      })
    );
  }

  transforms.push(
    meshopt({
      encoder: MeshoptEncoder,
      level: preset.meshoptLevel,
      quantizePosition: preset.quantizePosition,
      quantizeNormal: preset.quantizeNormal,
      quantizeTexcoord: preset.quantizeTexcoord,
      quantizeColor: preset.quantizeColor,
      quantizeWeight: preset.quantizeWeight,
      quantizeGeneric: preset.quantizeGeneric,
    })
  );

  await document.transform(...transforms);
  const outputBytes = await io.writeBinary(document);
  const suffix = preset.value === "extreme" ? "-extreme-compressed.glb" : "-compressed.glb";
  const compressedName = file.name.replace(/\.glb$/i, suffix);
  return new File([outputBytes], compressedName, {
    type: "model/gltf-binary",
  });
}
